import {forwardRef, useImperativeHandle, useRef, useState} from "react"
import classes from "./Accordion.module.scss"

const AccordionItem = forwardRef((props, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const headerId = `accordion-${props.accordionId}-header-${props.index}`;
    const contentId = `accordion-${props.accordionId}-content-${props.index}`;
    const TitleTag = props.titleTag ?? "h3"

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
        props.onCloseAll?.();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleAccordion();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            props.onFocusNext?.(props.index);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            props.onFocusPrev?.(props.index);
        } else if (e.key === 'Home') {
            e.preventDefault();
            props.onFocusFirst?.();
        } else if (e.key === 'End') {
            e.preventDefault();
            props.onFocusLast?.();
        }
    };

    useImperativeHandle(ref, () => ({
        close() {
            if (isOpen === false) {
                return;
            }
            setIsOpen(false);
        },
        focus() {
            document.getElementById(headerId)?.focus();
        }
    }));

    return (
        <div className={`${classes.item} ${isOpen ? classes.open : ''}`}>
            <TitleTag className={classes.headerWrapper}>
                <button
                    id={headerId}
                    className={classes.header}
                    onClick={toggleAccordion}
                    onKeyDown={handleKeyDown}
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    tabIndex={0}
                >
                    <span className={classes.title + " headline_3"}>{props.item.title}</span>
                    <span className={classes.caret  + " um-accordion-carret"} aria-hidden="true"></span>
                </button>
            </TitleTag>
            <div
                id={contentId}
                className={classes.contentWrapper + " um-accordion-content-wrapper"}
                aria-labelledby={headerId}
                role="region"
                hidden={!isOpen}
            >
                <div className={classes.content + " um-accordion-content"}>
                    <div dangerouslySetInnerHTML={{__html: props.item.content}}></div>
                    {props.item.link &&
                        <a href={props.item.link.href} target={props.item.link.target} title={props.item.link.text} className={"link " + props.item.linkClass}>{props.item.link.text}</a>
                    }
                </div>
            </div>
        </div>
    );
});

export const Accordion = (props) => {
    let items = [];
    const itemRefs = useRef([]);
    const TitleTag = props.titleTag ?? "h2"

    const initAccordion = () => {
        items = [];

        for (const child of props.children) {
            const link = child.querySelector('.link');

            items.push({
                title: child.querySelector('.title').innerText,
                content: child.querySelector('.content').innerHTML,
                link: link === null ? undefined : {
                    href: link.href,
                    text: link.textContent,
                    target: link.target
                },
                linkClass: link === null ? undefined : child.querySelector('.link-class')?.innerText,
            });
        }
    };

    initAccordion();

    const onCloseAll = (index) => {
        itemRefs.current.forEach((itemRef, i) => {
          if (i === index) {
              return true;
          }
          itemRef.close();
        });
    };

    const focusItem = (index) => {
        console.log("focusItem", index, itemRefs, itemRefs.current[index])
        if (index >= 0 && index < itemRefs.current.length) {
            itemRefs.current[index].focus();
        }
    };

    const handleFocusNext = (currentIndex) => {
        console.log("handle Focus Next")
        focusItem((currentIndex + 1) % itemRefs.current.length);
    };

    const handleFocusPrev = (currentIndex) => {
        focusItem((currentIndex - 1 + itemRefs.current.length) % itemRefs.current.length);
    };

    const handleFocusFirst = () => {
        focusItem(0);
    };

    const handleFocusLast = () => {
        focusItem(itemRefs.current.length - 1);
    };

    return (
        <div className={classes.accordion} role="presentation">
            {items.map((item, index) => (
                <AccordionItem
                    ref={el => itemRefs.current[index] = el}
                    onCloseAll={_ => onCloseAll(index)}
                    key={index}
                    item={item}
                    index={index}
                    accordionId={props.index}
                    onFocusNext={handleFocusNext}
                    onFocusPrev={handleFocusPrev}
                    onFocusFirst={handleFocusFirst}
                    onFocusLast={handleFocusLast}
                    titleTag={TitleTag}
                />
            ))}
        </div>
    );
};
